import React, { useMemo } from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@shared/ui/sidebar';
import { Button } from '@shared/ui/button';
import { SidebarSuiteHeader } from './SidebarSuiteHeader';
import { SidebarNavGroup } from './SidebarNavGroup';
import { useActiveSuite } from '../useActiveSuite';
import { useGroupCollapse } from './useGroupCollapse';
import { useDynamicNav } from './useDynamicNav';
import { SuiteNavItem } from '@suites/manifest';

function groupNavItems(nav?: SuiteNavItem[]) {
  if (!nav) return [];
  const groups: { name?: string; items: SuiteNavItem[] }[] = [];
  const map = new Map<string, SuiteNavItem[]>();
  const ungrouped: SuiteNavItem[] = [];

  for (const item of nav) {
    if (item.group) {
      if (!map.has(item.group)) {
        map.set(item.group, []);
      }
      map.get(item.group)!.push(item);
    } else {
      ungrouped.push(item);
    }
  }

  if (ungrouped.length > 0) groups.push({ items: ungrouped });
  map.forEach((items, name) => groups.push({ name, items }));
  return groups;
}

export const AppSidebar: React.FC = () => {
  const activeSuite = useActiveSuite();
  const dynamicNav = useDynamicNav(activeSuite);
  const { state } = useSidebar();
  const isCollapsed = state === 'collapsed';

  const groupedNav = useMemo(() => groupNavItems(dynamicNav), [dynamicNav]);

  const groupNames = useMemo(() => {
    return groupedNav
      .map((g) => g.name)
      .filter((name): name is string => typeof name === 'string' && name.length > 0);
  }, [groupedNav]);

  const { isGroupOpen, toggleGroup, toggleAll, hasAnyOpen } = useGroupCollapse(
    activeSuite?.id,
    groupNames
  );

  if (!activeSuite) return null;

  return (
    <Sidebar
      collapsible="icon"
      className="top-[var(--shell-header-h)] h-[calc(100svh-var(--shell-header-h))] border-r border-sidebar-border bg-sidebar z-20"
    >
      <SidebarSuiteHeader />
      <SidebarContent className="py-2">
        {groupedNav.map((group, idx) => {
          const gName = group.name;
          return (
            <SidebarNavGroup
              key={gName || `group_${idx}`}
              groupName={gName}
              items={group.items}
              isOpen={gName ? isGroupOpen(gName) : true}
              onToggle={gName ? () => toggleGroup(gName) : undefined}
            />
          );
        })}
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <div className="flex items-center justify-between w-full">
          <SidebarTrigger className="h-7 w-7 text-muted-foreground hover:text-foreground" />
          {!isCollapsed && groupNames.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleAll}
              className="h-7 px-2 text-[11px] font-medium text-muted-foreground hover:text-foreground flex items-center gap-1.5 transition-colors cursor-pointer"
              title={hasAnyOpen ? 'Collapse all subcategories' : 'Open all subcategories'}
            >
              {hasAnyOpen ? (
                <>
                  <ChevronsDownUp className="h-3.5 w-3.5" />
                  <span>Collapse All</span>
                </>
              ) : (
                <>
                  <ChevronsUpDown className="h-3.5 w-3.5" />
                  <span>Open All</span>
                </>
              )}
            </Button>
          )}
        </div>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};
